import { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { TpvDialog } from "../../../components/TpvDialog";
import {
  getOrderDetails,
  updateQuantityProductToPendingOrder,
  removeProductToPendingOrder,
} from "../../../api/orders";
import { updateOrder } from "../../../reducers/userConfigSlice";
import { flushAllProductChanges } from "../../../services/productSyncQueue";
import { canEditConfirmedOrder } from "../../../utils/orderState";
import { formatMoney } from "../../../utils/pricing";
export const ConfirmedLineEditor = ({ line, onClose }) => {
  const order = useSelector((s) => s.aplicationConfig.userConfig.order),
    dispatch = useDispatch();
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [quantity, setQuantity] = useState(Number(line.quantity)),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const pending = useRef(false),
    applied = useRef(false);
  const save = async (remove = false) => {
    if (pending.current) return;
    pending.current = true;
    setBusy(true);
    setError("");
    try {
      if (!(await flushAllProductChanges()))
        throw Error("Espera a que terminen los cambios pendientes.");
      const before = await getOrderDetails(order.orderId),
        fresh = before?.data;
      if (before?.error !== false || fresh?.orderId !== order.orderId)
        throw Error(
          "No se pudo actualizar el pedido. No se ha enviado ningún cambio.",
        );
      dispatch(updateOrder(fresh));
      if (applied.current) {
        onClose();
        return;
      }
      if (!canEditConfirmedOrder(fresh))
        throw Error(
          "Cocina ha iniciado el pedido o su estado ha cambiado. Ya no se puede modificar.",
        );
      const current = fresh.products.find(
        (p) => p.orderProductId === line.orderProductId,
      );
      if (!current || Number(current.quantity) !== Number(line.quantity))
        throw Error(
          "Esta línea ha cambiado. Cierra y vuelve a abrirla antes de editar.",
        );
      const payload = {
        productId: current.productId,
        variantId: current.variantId || null,
        itemIds: current.itemIds || [],
        note: current.note || "",
        ...(!remove ? { quantity } : {}),
      };
      const response = await (
        remove
          ? removeProductToPendingOrder
          : updateQuantityProductToPendingOrder
      )(payload, order.orderId);
      if (response?.error !== false) {
        const check = await getOrderDetails(order.orderId);
        if (check?.error === false && check.data?.orderId === order.orderId)
          dispatch(updateOrder(check.data));
        throw Error(
          "No se confirmó el cambio. Revisa la línea antes de volver a editar.",
        );
      }
      applied.current = true;
      const after = await getOrderDetails(order.orderId);
      if (after?.error !== false || after.data?.orderId !== order.orderId)
        throw Error(
          "El cambio se guardó. Actualiza el pedido para ver el resultado.",
        );
      dispatch(updateOrder(after.data));
      onClose();
    } catch (e) {
      setError(e.message || "No se pudo guardar el cambio.");
    } finally {
      pending.current = false;
      setBusy(false);
    }
  };
  const currentLine = order.products?.find(p => p.orderProductId === line.orderProductId) || line;
  const savedTotal = currentLine.lineTotalWithTax;
  const unitPrice = currentLine.unitPriceWithTax;
  return (
    <TpvDialog
      title={line.Product?.name || "Producto del pedido"}
      busy={busy}
      onClose={onClose}
      className="tpvConfirmedLineEditor tpvProductLineEditor"
      subtitle={Number.isFinite(unitPrice) ? `${formatMoney(unitPrice)} por unidad` : "Precio por unidad no disponible"}
      headerTrailing={<strong className="tpvProductLineTotal" title="Importe guardado">{formatMoney(savedTotal)}</strong>}
    >
      <div className="tpvDialogBody">
        <div className="tpvLineQuantity">
          <strong>CANTIDAD</strong>
          <button type="button" aria-label="Menos unidades" disabled={busy || applied.current || quantity <= 1} onClick={() => setQuantity(q => Math.max(1, q - 1))}>−</button>
          <input aria-label="Cantidad" type="number" min="1" max="999" step="1" value={quantity} disabled={busy || applied.current} onChange={event => setQuantity(Number(event.target.value))} />
          <button type="button" aria-label="Más unidades" disabled={busy || applied.current || quantity >= 999} onClick={() => setQuantity(q => Math.min(999, q + 1))}>+</button>
        </div>
        <button type="button" className="tpvLineOptions" disabled={!line.info || busy} aria-expanded={optionsOpen} onClick={() => setOptionsOpen(open => !open)}><small>OPCIONES GUARDADAS</small><strong>{line.info || "Sin opciones"}</strong><span aria-hidden="true">›</span></button>
        {optionsOpen && <p className="tpvLineSavedInfo">{line.info}</p>}
        <label className="tpvLineNote">NOTA DE COCINA <small>Guardada</small><textarea aria-label="Nota guardada de cocina" readOnly value={line.note || ""} placeholder="Sin nota guardada" /></label>
        <p className="tpvLineSavedInfo">El importe se actualizará al guardar la cantidad. Las opciones y notas guardadas se conservan.</p>
        {error && <p role="alert">{error}</p>}
        <button
          type="button"
          className="tpvLineDelete"
          disabled={busy || applied.current || !canEditConfirmedOrder(order)}
          onClick={() => save(true)}
        >
          Eliminar del pedido
        </button>
      </div>
      <footer>
        <button
          type="button"
          className="tpvPrimary"
          disabled={
            busy ||
            (!applied.current &&
              (!canEditConfirmedOrder(order) ||
                !Number.isInteger(quantity) ||
                quantity < 1 ||
                quantity > 999))
          }
          onClick={() => save()}
        >
          {busy
            ? "Guardando…"
            : applied.current
              ? "Actualizar pedido"
              : "LISTO"}
        </button>

      </footer>
    </TpvDialog>
  );
};
